import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { ProductType, useCreateAdMutation } from '@/models/ad';
import { getFileType, uploadFile } from '@/features/upload';
import { LocationSelector } from '@/features/location';
import { Trans, useTranslation } from 'react-i18next';
import { MediaSection, BasicInfoSection } from './components';
import { Summary } from '../../features/summary/components/Summary';
import { PaymentModelSection } from '@/pages/ad-pages/components/PaymentModelSection';
import { Typography } from '@mui/material';
import { css, cx } from '@emotion/css';
import { MapType } from '@/features/map';
import { useNavigate } from 'react-router';
import { z, ZodType } from 'zod'; // Add new import
import { zodResolver } from '@hookform/resolvers/zod';
import { StartEndDateSection } from '@/pages/ad-pages/components/StartEndDateSection';
// import { DevTool } from '@hookform/devtools';
import { isFetchBaseQueryError } from '@/api/utils';
import { StatusCodesList } from '@/api';
import { useCookies } from 'react-cookie';
import { useCreateCampaignMutation } from '@/models/campaign';
import { CampaignSection } from '@/pages/ad-pages/components/CampaignSection';
import dayjs from 'dayjs';
import { DisplayHoursSection } from '@/pages/ad-pages/components/DisplayHoursSection';
import { useFlag } from '@/commons/tracking';
import { PaymentModel } from './constants';
import { useProfileQuery } from '@/models/user';
import { ProfileSnackbar } from '@/components/ProfileSnackbar';
import { ProfileIncompleteAlert } from '@/components/ProfileIncompleteAlert';

type CampaignFormData = {
  id: string | null;
  title: string;
};

export type CreateAdFormData = {
  title: string;
  campaign: CampaignFormData;
  displays?: number;
  views?: number;
  qrUrl?: string;
  qrText?: string;
  startDate: Date;
  endDate: Date;
  startHour?: string;
  endHour?: string;
};

export const CreateAd = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [countryId, setCountryId] = useState<number | undefined>();
  const [areaId, setAreaId] = useState<number | undefined>();
  const [selectedZones, setSelectedZones] = useState<number[]>([]);
  const [format, setFormat] = useState<string>('');
  const [file, setFile] = useState<File | null>(null);
  const [productType, setProductType] = useState(ProductType.Image);
  const [uploading, setUploading] = useState<boolean>(false);
  const [cookies] = useCookies(['token']);
  const [createCampaign] = useCreateCampaignMutation();
  const { data: user } = useProfileQuery();
  const [showProfileSnackbar, setShowProfileSnackbar] = useState(false);
  const [showProfileAlert, setShowProfileAlert] = useState<boolean>(
    !user?.isProfileComplete
  );
  const { value: isTimeRangeEnabled } = useFlag(
    'create_ad_time_range_picker',
    'off'
  );

  const schema: ZodType<CreateAdFormData> = z
    .object({
      title: z.string().refine((value) => value.trim() !== '', {
        message: t('create_ad.form.title.required'),
      }),
      campaign: z.object({
        id: z.string().nullable(),
        title: z.string().refine((value) => value.trim() !== '', {
          message: t('create_ad.form.campaign.required'),
        }),
      }),
      displays: z.coerce
        .number()
        .min(1000, {
          message: t(
            'create_ad.payment_model.ppd.calculation_model.displays_to_buy.min_error'
          ),
        })
        .optional(),
      views: z.coerce
        .number()
        .min(100, {
          message: t(
            'create_ad.payment_model.ppv.calculation_model.views_to_buy.min_error'
          ),
        })
        .optional(),
      qrUrl: z
        .string()
        .optional()
        .refine((value) => !value || value.startsWith('https://'), {
          message: t('create_ad.form.qr.url_required_format'),
        }),
      qrText: z.string().optional(),
      startDate: z.date().refine((value) => value !== null, {
        message: t('create_ad.form.startDate.required'),
      }),
      endDate: z.date().refine((value) => value !== null, {
        message: t('create_ad.form.endDate.required'),
      }),
      startHour: z.string().optional(),
      endHour: z.string().optional(),
    })
    .refine(
      (data) => {
        return data.displays !== undefined || data.views !== undefined;
      },
      {
        message: t('create_ad.form.payment_model.one_required'),
        path: ['displays'],
      }
    );

  const {
    control,
    trigger,
    formState: { errors },
    handleSubmit,
    watch,
    setValue,
  } = useForm<CreateAdFormData>({
    resolver: zodResolver(schema),
    mode: 'onSubmit',
    defaultValues: {
      startDate: dayjs().startOf('day').toDate(),
      endDate: dayjs().add(1, 'day').endOf('day').toDate(),
      startHour: isTimeRangeEnabled === 'on' ? '19:00:00' : undefined,
      endHour: isTimeRangeEnabled === 'on' ? '07:00:00' : undefined,
      displays: 1000,
    },
  });

  const [
    createAd,
    {
      isError: isCreateAdError,
      error: createAdError,
      isSuccess: isCreateAdSuccess,
    },
  ] = useCreateAdMutation();

  useEffect(() => {
    if (user && !user.isProfileComplete) {
      setShowProfileSnackbar(true);
    }
  }, [user]);

  useEffect(() => {
    if (isCreateAdError && isFetchBaseQueryError(createAdError)) {
      const errorData = createAdError.data as { code: number };

      if (errorData.code === StatusCodesList.NotEnoughFunds) {
        navigate('/client/wallet');
      }
    }
  }, [isCreateAdError, createAdError, navigate]);

  useEffect(() => {
    if (isCreateAdSuccess) {
      navigate('/client/review/ads');
    }
  }, [isCreateAdSuccess, navigate]);

  const handleFileUpload = async (file: File) => {
    const uploadedFile = await uploadFile({
      file,
      beforeCallback: () => setUploading(true),
      afterCallback: () => setUploading(false),
      token: cookies.token,
      productType,
    });

    return uploadedFile?.file.Location;
  };

  const handleTypeSelect = (selected: ProductType) => {
    setProductType(selected);
    setFormat('');
  };

  const handleUpload = (file: File) => {
    setFile(file);

    const fileType = getFileType(file);
    if (fileType) setFormat(fileType);
  };

  const onCreate = async (data: CreateAdFormData) => {
    const validated = await trigger();

    if (validated && file) {
      const url = await handleFileUpload(file);
      let campaignResult;

      if (!data.campaign.id) {
        campaignResult = await createCampaign({
          title: data.campaign.title,
        }).unwrap();
      }

      const {
        title,
        qrUrl,
        qrText,
        displays,
        views,
        startDate,
        endDate,
        startHour,
        endHour,
      } = data;

      if (url) {
        const ad = {
          title,
          type: productType,
          zones: selectedZones,
          url,
          format,
          qrUrl: qrUrl ? qrUrl : undefined,
          qrText: qrText ? qrText : undefined,
          displays,
          views,
          startDate,
          endDate,
          startHour,
          endHour,
          campaignId: data.campaign.id || campaignResult?.result.id,
        };

        await createAd(ad);
      }
    }
  };

  const onModelChange = (model: PaymentModel) => {
    if (model === PaymentModel.PPD) {
      setValue('displays', 1000);
      setValue('views', undefined);
    }
    if (model === PaymentModel.PPV) {
      setValue('views', 100);
      setValue('displays', undefined);
    }
  };

  const handleZonesSelect = (zones: number[]) => {
    setSelectedZones(zones);
  };

  return (
    <>
      <ProfileIncompleteAlert
        message={<Trans i18nKey="create_ad.profile_incomplete_alert" />}
        open={showProfileAlert}
        onClose={() => setShowProfileAlert(false)}
      />
      <div
        className={cx(
          'xl:flex w-full h-full pb-28 md:gap-4 xl:overflow-auto',
          css`
            -ms-overflow-style: none;
            scrollbar-width: none;

            &::-webkit-scrollbar {
              display: none;
            }
          `
        )}
      >
        <div className="xl:flex-1 xl:min-h-0">
          <div className="mb-4 md:mb-8">
            <Typography variant="h4" fontWeight={500}>
              <Trans i18nKey="create_ad.page.title" />
            </Typography>
          </div>

          <div className="w-full mb-8">
            <MediaSection
              productType={productType}
              onTypeSelect={handleTypeSelect}
              onUpload={handleUpload}
              qrUrl={watch('qrUrl')}
              qrText={watch('qrText')}
              control={control}
              errors={errors}
            />
          </div>

          <div className="mb-8">
            <CampaignSection control={control} errors={errors} />
          </div>

          <div className="mb-8">
            <BasicInfoSection control={control} errors={errors} />
          </div>

          <div className="flex-1 mb-8">
            <LocationSelector
              control={control}
              selectedZones={selectedZones}
              areaId={areaId}
              countryId={countryId}
              mapType={MapType.WithSelection}
              onChangeCountry={(id?: number) => {
                setCountryId(id);
                setAreaId(undefined);
              }}
              onChangeArea={(id?: number) => {
                setAreaId(id);
                setSelectedZones([]);
              }}
              onZonesSelect={handleZonesSelect}
            />
          </div>

          <div className="mb-8">
            <PaymentModelSection
              control={control}
              errors={errors}
              onModelChange={onModelChange}
            />
          </div>

          <div className="md:pb-16 mb-8 flex flex-col 2xl:flex-row gap-4">
            <div>
              <StartEndDateSection
                control={control}
                errors={errors}
                displays={watch('displays')}
                views={watch('views')}
                valueFrom={watch('startDate')}
                valueTo={watch('endDate')}
                setValueFrom={(date) => date && setValue('startDate', date)}
                setValueTo={(date) => date && setValue('endDate', date)}
              />
            </div>
            {isTimeRangeEnabled === 'on' ? (
              <div className="flex-1/3">
                <DisplayHoursSection
                  control={control}
                  errors={errors}
                  valueFrom={watch('startHour')}
                  valueTo={watch('endHour')}
                  setValueFrom={(time) => time && setValue('startHour', time)}
                  setValueTo={(time) => time && setValue('endHour', time)}
                />
              </div>
            ) : null}
          </div>
        </div>

        <div className="xl:sticky xl:top-4 xl:self-start xl:grow-0 xl:shrink-0 xl:basis-3/12">
          <Summary
            type={productType}
            zones={selectedZones}
            displays={watch('displays')}
            views={watch('views')}
            countryId={countryId}
            areaId={areaId}
            isError={Object.keys(errors).length > 0}
            onCreate={handleSubmit(onCreate)}
            isUploadingVideo={uploading}
            onSetValue={setValue}
            reasonMessage={
              !user?.isProfileComplete
                ? t('create_ad.profile_incomplete_message')
                : undefined
            }
          />
        </div>
        <ProfileSnackbar
          open={showProfileSnackbar}
          onClose={() => setShowProfileSnackbar(false)}
        />

        {/* <DevTool control={control} /> */}
      </div>
    </>
  );
};
